// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Answers: which banner is actually dearer? A brand × region matrix of the
// premium over the local market, because comparing brands on raw price would
// just be measuring where their stations happen to be.

import { loadBrands } from '../data';
import type { Brand, BrandData } from '../types';
import { chartSvg, mark, svgEl, text } from '../chart';
import { gridCells } from '../layout';
import { cents, deltaLabel, esc, escTip, tankSaving } from '../format';
import { glossaryTerm } from '../glossary';
import { openOverlay } from '../components/drawer';

const MAX_BRANDS = 22;

export async function renderBrands(root: HTMLElement): Promise<void> {
  root.innerHTML = '<div class="skeleton"></div>';
  const data = await loadBrands();
  const brands = data.brands.filter((b) => b.brand && b.n >= 400).slice(0, MAX_BRANDS);

  root.innerHTML = `
    <div class="view-head">
      <h1>Which banner charges more</h1>
      <p>Every brand's ${glossaryTerm('premium', 'premium')} over the middle of the market in the same region,
      so a brand is not penalised for having remote stations. Green is cheaper than the local market, red is dearer.</p>
    </div>
    <div id="brand-cards"></div>
    <div class="panel">
      <div class="panel-head">
        <h2>Brand by region</h2>
        <p>Cents per litre above or below the middle unleaded price in that region, over the last four months.
        Blank cells mean that brand has no station reporting there. Click a cell or a brand for detail.</p>
      </div>
      <div class="chart-scroll"><div class="chart-frame" id="brand-matrix"></div></div>
      <div class="legend" id="brand-legend"></div>
    </div>
    <div class="panel">
      <div class="panel-head"><h2>Overall ranking</h2><p>Weighted by how many price readings each brand contributed.</p></div>
      <div class="chart-scroll"><div class="chart-frame" id="brand-bars"></div></div>
    </div>
  `;

  renderCards(root.querySelector('#brand-cards')!, brands);
  renderMatrix(root.querySelector('#brand-matrix') as HTMLElement, brands, data, root.querySelector('#brand-legend') as HTMLElement);
  renderBars(root.querySelector('#brand-bars') as HTMLElement, brands);
}

function renderCards(host: HTMLElement, brands: Brand[]): void {
  if (!brands.length) { host.innerHTML = ''; return; }
  const sorted = [...brands].sort((a, b) => a.premium - b.premium);
  const cheap = sorted[0];
  const dear = sorted[sorted.length - 1];
  const widest = [...brands].sort((a, b) => {
    const spread = (x: Brand) => Math.max(...x.cells.map((c) => c.premium)) - Math.min(...x.cells.map((c) => c.premium));
    return spread(b) - spread(a);
  })[0];
  const wSpread = Math.max(...widest.cells.map((c) => c.premium)) - Math.min(...widest.cells.map((c) => c.premium));
  host.innerHTML = `
    <div class="grid-cards" style="margin-bottom:var(--space-lg)">
      <div class="stat-card"><b>${deltaLabel(cheap.premium)}</b><span class="truncate">cheapest banner — ${esc(cheap.brand)}</span></div>
      <div class="stat-card"><b>${deltaLabel(dear.premium)}</b><span class="truncate">dearest banner — ${esc(dear.brand)}</span></div>
      <div class="stat-card"><b>${tankSaving(dear.premium - cheap.premium)}</b><span>a tank, dearest vs cheapest banner</span></div>
      <div class="stat-card"><b>${cents(wSpread)}c</b><span class="truncate">${esc(widest.brand)} varies most by region</span></div>
    </div>
  `;
}

function premiumColour(v: number): string {
  const t = Math.max(-1, Math.min(1, v / 60));
  if (t < -0.55) return '#167a4a';
  if (t < -0.2) return '#6aa84f';
  if (t < 0.2) return '#e8e3da';
  if (t < 0.55) return '#dd8a3c';
  return '#c2410c';
}

function renderMatrix(host: HTMLElement, brands: Brand[], data: BrandData, legendHost: HTMLElement): void {
  const regions = data.regions
    .filter((r) => brands.some((b) => b.cells.some((c) => c.region === r.region)))
    .map((r) => r.region);
  if (!brands.length || !regions.length) { host.innerHTML = '<p class="empty">Not enough brand data.</p>'; return; }

  const cellW = 62;
  const rowH = 26;
  const padL = 132;
  const padT = 132;
  const padR = 12;
  const padB = 16;
  const gridW = regions.length * cellW;
  const gridH = brands.length * rowH;
  const width = padL + gridW + padR;
  const height = padT + gridH + padB;

  const svg = chartSvg(width, height, 'Brand price premium by region');
  const cells = gridCells(regions.length, brands.length, gridW, gridH, 2);

  for (const cell of cells) {
    const brand = brands[cell.row];
    const region = regions[cell.col];
    const hit = brand.cells.find((c) => c.region === region);
    const rect = svgEl('rect', {
      x: padL + cell.x, y: padT + cell.y, width: cell.w, height: cell.h, rx: 3,
      fill: hit ? premiumColour(hit.premium) : '#f7f5f1',
      stroke: '#ffffff', 'stroke-width': 0.5,
    });
    if (hit) {
      mark(rect, `${brand.brand} — ${region}\n${deltaLabel(hit.premium)} vs the local market\nmiddle price ${cents(hit.med)}c\n${hit.n.toLocaleString()} readings`);
      rect.addEventListener('click', () => openBrand(brand, region));
    } else {
      rect.setAttribute('aria-label', `${brand.brand} has no station reporting in ${region}`);
    }
    svg.appendChild(rect);
    if (hit && cell.w > 40 && Math.abs(hit.premium) >= 10) {
      const t = text(padL + cell.x + cell.w / 2, padT + cell.y + cell.h / 2 + 4, deltaLabel(hit.premium).replace('c', ''), {
        'text-anchor': 'middle', 'font-size': 10.5, fill: Math.abs(hit.premium) > 33 ? '#ffffff' : '#3f3a34',
        'pointer-events': 'none',
      });
      svg.appendChild(t);
    }
  }

  brands.forEach((b, i) => {
    const label = text(padL - 8, padT + i * rowH + rowH / 2 + 4, b.brand.length > 18 ? `${b.brand.slice(0, 17)}…` : b.brand, {
      'text-anchor': 'end', 'font-size': 12, cursor: 'pointer',
    });
    label.classList.add('mark');
    label.setAttribute('data-tip', `${b.brand}\n${deltaLabel(b.premium)} vs local markets overall\n${b.cells.length} regions, ${b.n.toLocaleString()} readings`);
    label.addEventListener('click', () => openBrand(b));
    svg.appendChild(label);
  });

  regions.forEach((r, i) => {
    const x = padL + (i + 0.5) * cellW;
    const t = text(x, padT - 10, r.length > 22 ? `${r.slice(0, 21)}…` : r, { 'font-size': 11.5, transform: `rotate(-42 ${x} ${padT - 10})` });
    svg.appendChild(t);
  });

  host.appendChild(svg);
  legendHost.innerHTML = `
    <span class="key"><span class="swatch" style="background:#167a4a"></span>more than 3c cheaper than the local market</span>
    <span class="key"><span class="swatch" style="background:#e8e3da"></span>about the local market</span>
    <span class="key"><span class="swatch" style="background:#c2410c"></span>more than 3c dearer</span>
    <span class="key"><span class="swatch" style="background:#f7f5f1;border:1px solid var(--border-default)"></span>no station reporting</span>
  `;
}

function renderBars(host: HTMLElement, brands: Brand[]): void {
  const sorted = [...brands].sort((a, b) => a.premium - b.premium);
  const width = 780;
  const rowH = 26;
  const padL = 148;
  const padR = 70;
  const height = sorted.length * rowH + 42;
  const innerW = width - padL - padR;
  const lim = Math.max(10, ...sorted.map((b) => Math.abs(b.premium))) * 1.1;
  const zero = padL + innerW / 2;
  const xFor = (v: number) => zero + (v / lim) * (innerW / 2);

  const svg = chartSvg(width, height, 'Brand premium ranking');
  svg.appendChild(svgEl('line', { x1: zero, x2: zero, y1: 22, y2: height - 20, stroke: '#a8a094' }));
  svg.appendChild(text(zero, 14, 'the middle of the local market', { 'text-anchor': 'middle', 'font-size': 11, fill: '#857f78' }));

  sorted.forEach((b, i) => {
    const y = 24 + i * rowH;
    const x0 = Math.min(zero, xFor(b.premium));
    const w = Math.max(1.5, Math.abs(xFor(b.premium) - zero));
    const rect = svgEl('rect', { x: x0, y: y + 4, width: w, height: rowH - 12, rx: 2, fill: b.premium <= 0 ? '#167a4a' : '#c2410c' });
    mark(rect, `${b.brand}\n${deltaLabel(b.premium)} vs the local market\n${tankSaving(b.premium)} on a 55-litre tank\n${b.cells.length} regions, ${b.n.toLocaleString()} readings`);
    rect.addEventListener('click', () => openBrand(b));
    svg.appendChild(rect);
    svg.appendChild(text(padL - 8, y + rowH / 2 + 2, b.brand.length > 20 ? `${b.brand.slice(0, 19)}…` : b.brand, { 'text-anchor': 'end', 'font-size': 12 }));
    svg.appendChild(text(width - padR + 6, y + rowH / 2 + 2, deltaLabel(b.premium), { 'font-size': 12, fill: b.premium <= 0 ? '#167a4a' : '#c2410c' }));
  });
  host.appendChild(svg);
  void escTip;
}

function openBrand(brand: Brand, focusRegion?: string): void {
  const rows = [...brand.cells].sort((a, b) => a.premium - b.premium).map((c) => `
    <tr${c.region === focusRegion ? ' style="background:var(--accent-soft)"' : ''}>
      <td>${esc(c.region)}</td>
      <td class="n">${cents(c.med)}c</td>
      <td class="n" style="color:${c.premium <= 0 ? 'var(--status-good)' : 'var(--status-bad)'}">${deltaLabel(c.premium)}</td>
      <td class="n">${c.n.toLocaleString()}</td>
    </tr>`).join('');
  openOverlay('drawer', `
    <h2>${esc(brand.brand)}</h2>
    <p class="sub">${deltaLabel(brand.premium)} against the middle of the local market, across ${brand.cells.length} regions</p>
    <div class="table-scroll" style="margin-top:var(--space-lg)">
      <table>
        <thead><tr><th>Region</th><th class="n">Middle price</th><th class="n">Premium</th><th class="n">Readings</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <p class="brand-sub" style="margin-top:var(--space-md)">
      Premium is measured against the middle unleaded price in the same region over the same four months,
      so it reflects the banner's pricing rather than where its stations happen to be.
    </p>
  `, brand.brand);
}
